<!-- Compose — the critical path. To, Message, codebook, presets, a preview on
     your own device, and one action: Send page. Screens role. -->
<template>
  <IonPage>
    <AppHeader title="New page" back="/tabs/pager" />

    <IonContent :fullscreen="true" class="pm-scr-content">
      <div class="pm-scr-pad pm-scr-compose">
        <div class="pm-scr-field">
          <label class="pm-scr-label" :for="`${uid}-compose-to`">To</label>
          <select
            :id="`${uid}-compose-to`"
            class="pm-scr-input pm-scr-select"
            :value="to"
            :aria-invalid="toError ? 'true' : 'false'"
            :aria-describedby="toError ? `${uid}-compose-to-err` : undefined"
            @change="onToChange($event.target.value)"
          >
            <option value="" disabled>Choose a friend or group</option>
            <optgroup v-if="groups.length" label="Groups">
              <option v-for="g in groups" :key="g.id" :value="`group-${g.id}`">
                {{ g.name }} · {{ g.members.length - 1 }} {{ g.members.length === 2 ? 'other' : 'others' }}
              </option>
            </optgroup>
            <optgroup label="Friends">
              <option v-for="f in acceptedFriends" :key="f.number" :value="f.number">
                {{ toLabel(f.number) }}
              </option>
              <option v-for="f in pendingOut" :key="f.number" :value="f.number" disabled>
                {{ friendName(f.number) }} — Waiting for them to accept
              </option>
            </optgroup>
          </select>
          <p v-if="toError" :id="`${uid}-compose-to-err`" class="field-error" role="alert">{{ toError }}</p>
          <p v-else-if="toGroup" class="pm-scr-muted pm-scr-small">One page to {{ groupMembersLine }}.</p>
        </div>

        <div class="pm-scr-field">
          <label class="pm-scr-label" :for="`${uid}-compose-body`">Message</label>
          <textarea
            :id="`${uid}-compose-body`"
            ref="field"
            :value="body"
            class="pm-scr-input pm-scr-textarea pm-scr-input--lcd"
            rows="3"
            placeholder="WHAT'S UP"
            autocapitalize="characters"
            :aria-invalid="bodyError ? 'true' : 'false'"
            :aria-describedby="bodyError ? `${uid}-compose-body-err ${uid}-compose-count` : `${uid}-compose-count`"
            @input="onInput"
          />
          <p v-if="bodyError" :id="`${uid}-compose-body-err`" class="field-error" role="alert">{{ bodyError }}</p>
          <p :id="`${uid}-compose-count`" class="pm-scr-counter" :class="{ 'is-warn': count >= WARN_AT }">
            <span v-if="limitHit" class="pm-scr-counter-note">Pages stop at {{ MAX_GRAPHEMES }}. </span>
            {{ count }}/{{ MAX_GRAPHEMES }}
          </p>
        </div>

        <h2 :id="`${uid}-compose-codes`" class="section-title">Codebook</h2>
        <div class="pm-scr-chips" role="group" :aria-labelledby="`${uid}-compose-codes`">
          <button
            v-for="c in quickCodes"
            :key="c.code"
            type="button"
            class="pm-scr-chip"
            :aria-label="`Insert ${c.code}, ${c.meaning}`"
            @click="insert(c.code)"
          >
            <span class="pm-scr-code">{{ c.code }}</span> {{ c.short }}
          </button>
          <button type="button" class="pm-scr-chip pm-scr-chip--more" @click="codebookOpen = true">All codes</button>
        </div>

        <h2 :id="`${uid}-compose-presets`" class="section-title">Presets</h2>
        <div class="pm-scr-chips" role="group" :aria-labelledby="`${uid}-compose-presets`">
          <button
            v-for="p in presets"
            :key="p.id"
            type="button"
            class="pm-scr-chip pm-scr-chip--preset"
            :aria-label="`Insert ${p.text}`"
            @click="insert(p.text)"
          >
            <PageText :body="p.text" />
          </button>
          <button type="button" class="pm-scr-chip pm-scr-chip--more" @click="router.push('/tabs/settings')">Edit presets</button>
        </div>

        <!-- The preview appears once there is something to preview: an empty
             LCD says nothing, and it pushed the form under the Send rail. -->
        <Transition name="pm-scr-reveal">
          <section v-if="body.trim()" class="pm-scr-compose-preview" aria-label="Preview">
            <h2 class="section-title">Preview</h2>
            <LcdPreview :device-id="settings.device" :body="body" :from="fromLabel" />
          </section>
        </Transition>
      </div>

      <ActionRail>
        <!-- Enabled whenever it is not already sending: a tap with something
             missing says what, beside the field, instead of a dead button. -->
        <IonButton expand="block" :disabled="send.busy.value" @click="trySend">
          {{ send.busy.value ? 'Sending…' : 'Send page' }}
        </IonButton>
        <p v-if="send.error.value" class="field-error" role="alert">{{ send.error.value }}</p>
      </ActionRail>
    </IonContent>

    <CodebookSheet :is-open="codebookOpen" @close="codebookOpen = false" @insert="insert" />
    <BeepsPrompt :is-open="beepsOpen" @close="afterBeeps" />
  </IonPage>
</template>

<script setup>
import { userMessage } from '../components/user-message.js';
import { ref, computed, watch, nextTick, onBeforeUnmount, useId } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonButton, useIonRouter, onIonViewDidEnter,
} from '@ionic/vue';
import { AppHeader, ActionRail, useAction, toast, announce } from '../core/ui/index.js';
import {
  me, acceptedFriends, pendingOut, settings, presets, drafts, beepsOffer, groups,
  friendName, friendByNumber, sendPage, groupById, sendGroupPage, groupDraftKey,
} from '../store.js';
import { formatNumber } from '../pm/format.js';
import { countGraphemes, MAX_GRAPHEMES } from '../pm/text.js';
import { CODEBOOK } from '../pm/codebook.js';
import PageText from '../components/PageText.vue';
import LcdPreview from '../components/LcdPreview.vue';
import CodebookSheet from '../components/CodebookSheet.vue';
import BeepsPrompt from '../components/BeepsPrompt.vue';

// Per-instance ids: Ionic keeps earlier pages in the DOM, so a fixed id
// would repeat and break label/for pairing.
const uid = `pm${useId()}`;

const props = defineProps({ number: { type: String, default: '' } });

const router = useRouter();
const ionRouter = useIonRouter();

const WARN_AT = 150;
// The six codes people actually send; the rest are one tap away in the sheet.
// Chip labels are the short form (the spec's "911 Urgent"); the accessible
// name and the All codes sheet carry the full meaning.
const QUICK = [
  ['143', 'I love you'], ['911', 'Urgent'], ['07734', 'Hello'],
  ['411', "What's up?"], ['121', 'Talk'], ['222', "Let's eat"],
];
const quickCodes = QUICK
  .map(([code, short]) => {
    const e = CODEBOOK.find((c) => c.code === code);
    return e ? { ...e, short } : null;
  })
  .filter(Boolean);

const to = ref('');
const body = ref('');
const field = ref(null);
const limitHit = ref(false);
const toError = ref('');
const bodyError = ref('');
const codebookOpen = ref(false);
const beepsOpen = ref(false);

const count = computed(() => countGraphemes(body.value));
const canSend = computed(() => !!to.value && body.value.trim().length > 0);
// The To value: a 10-digit number, or `group-<id>` for a group page.
const groupIdOf = (v) => (String(v || '').startsWith('group-') ? String(v).slice(6) : '');
const toGroup = computed(() => groupById(groupIdOf(to.value)));
const groupMembersLine = computed(() => (toGroup.value?.members ?? [])
  .filter((m) => m.number !== me.value?.number).map((m) => friendName(m.number)).join(', '));
const draftKey = (v) => (groupIdOf(v) ? groupDraftKey(groupIdOf(v)) : v);
const myName = computed(() => (me.value?.handle || (me.value?.number ? formatNumber(me.value.number) : 'YOU')).toUpperCase());
// The preview reads as it will on their pager: FROM ALEX, or FROM CREW · ALEX.
const fromLabel = computed(() => (toGroup.value ? `${toGroup.value.name} · ${myName.value}` : myName.value));

// ── To + drafts ─────────────────────────────────────────────────────────
function canPage(n) {
  if (groupIdOf(n)) return !!groupById(groupIdOf(n));
  return friendByNumber(n)?.status === 'accepted';
}

let draftTimer = 0;
function saveDraftNow() {
  clearTimeout(draftTimer);
  draftTimer = 0;
  if (to.value) drafts[draftKey(to.value)] = body.value;
}
function saveDraftSoon() {
  clearTimeout(draftTimer);
  draftTimer = setTimeout(saveDraftNow, 300);
}

function load(n) {
  toError.value = '';
  bodyError.value = '';
  if (n && !canPage(n)) {
    const f = friendByNumber(n);
    if (groupIdOf(n)) {
      toError.value = "You're not in that group any more.";
      to.value = '';
      return;
    }
    toError.value = f?.status === 'pending-out'
      ? `${friendName(n)} hasn't accepted yet. You can page them once they do.`
      : `${friendName(n)} isn't one of your friends yet.`;
    to.value = '';
    return;
  }
  to.value = n || '';
  body.value = (n && drafts[draftKey(n)]) || '';
}

function onToChange(n) {
  saveDraftNow();
  load(n);
}

watch(() => props.number, (n) => { saveDraftNow(); load(n || ''); }, { immediate: true });

// Arriving with the recipient already chosen (REPLY, a friend's Page button),
// the next thing to do is type: put the cursor in the message.
onIonViewDidEnter(() => {
  if (to.value && !body.value) {
    try { field.value?.focus({ preventScroll: true }); } catch { /* not focusable */ }
  }
});
onBeforeUnmount(saveDraftNow);

// ── Message ─────────────────────────────────────────────────────────────
function onInput(e) {
  const next = e.target.value;
  if (countGraphemes(next) > MAX_GRAPHEMES) {
    // Hard stop, never a silent truncation: put back what was there.
    const pos = Math.max(0, (e.target.selectionStart ?? next.length) - (next.length - body.value.length));
    e.target.value = body.value;
    try { e.target.setSelectionRange(pos, pos); } catch { /* not focused */ }
    if (!limitHit.value) announce(`Pages stop at ${MAX_GRAPHEMES} characters.`);
    limitHit.value = true;
    return;
  }
  limitHit.value = false;
  body.value = next;
  if (bodyError.value && next.trim()) bodyError.value = '';
  saveDraftSoon();
}

async function insert(text) {
  const el = field.value;
  const cur = body.value;
  const start = el?.selectionStart ?? cur.length;
  const end = el?.selectionEnd ?? cur.length;
  const before = cur.slice(0, start);
  const after = cur.slice(end);
  const pre = before && !/\s$/.test(before) ? ' ' : '';
  const post = after && !/^\s/.test(after) ? ' ' : '';
  const next = `${before}${pre}${text}${post}${after}`;
  if (countGraphemes(next) > MAX_GRAPHEMES) {
    limitHit.value = true;
    toast(`That won't fit — pages stop at ${MAX_GRAPHEMES}`);
    return;
  }
  limitHit.value = false;
  body.value = next;
  bodyError.value = '';
  saveDraftSoon();
  await nextTick();
  const caret = before.length + pre.length + text.length;
  try { el?.focus(); el?.setSelectionRange(caret, caret); } catch { /* ignore */ }
}

// ── Send ────────────────────────────────────────────────────────────────
function leave() {
  if (ionRouter.canGoBack()) ionRouter.back();
  else router.replace('/tabs/pager');
}

const send = useAction(async () => {
  if (!canSend.value) return;
  const recipient = to.value;
  const gid = groupIdOf(recipient);
  const r = gid ? await sendGroupPage(gid, body.value.trim()) : await sendPage(recipient, body.value.trim());
  if (!r?.ok) throw new Error(userMessage(r, "Couldn't send that page. Try again."));
  clearTimeout(draftTimer);
  drafts[draftKey(recipient)] = '';
  body.value = '';
  const queued = !!r.queued;
  const msg = queued ? 'QUEUED — WILL SEND WHEN ONLINE' : gid ? `SENT TO ${groupById(gid)?.name ?? 'GROUP'} ✓` : 'SENT ✓';
  toast(msg);
  announce(queued ? 'Queued. It will send when you are online.' : 'Page sent.');
  if (beepsOffer.value) beepsOpen.value = true;
  else leave();
}, { weight: 'pop' });

// Say what is missing, next to it, and put the cursor there.
function trySend() {
  if (!to.value) {
    toError.value = toError.value || 'Choose who to page — a friend or a group.';
    document.getElementById(`${uid}-compose-to`)?.focus();
    return;
  }
  if (!body.value.trim()) {
    bodyError.value = 'Write a page first — or tap a code below.';
    field.value?.focus();
    return;
  }
  send.run();
}

function afterBeeps() {
  if (!beepsOpen.value) return;
  beepsOpen.value = false;
  leave();
}

/** "MUM · 555-214-0987", or just the number when they have no name yet. */
function toLabel(number) {
  const num = formatNumber(number);
  const name = friendName(number);
  return name && name !== num && name !== number ? `${name} · ${num}` : num;
}
</script>
